/**
 * Table-of-contents outline for the {@link MarkdownEditor}. Purely
 * presentational: it renders the heading list produced by the
 * `@tiptap/extension-table-of-contents` extension and reports clicks back to
 * the editor, which owns scrolling and the URL hash.
 *
 * Two variants share one list renderer:
 * - `sidebar` — a fixed-width column pinned to the right of the editor. Used
 *   when the pane is wide enough that the TOC doesn't squeeze the prose.
 * - `overlay` — an absolutely-positioned panel that slides down from under the
 *   toolbar when toggled. Used on narrow panes so the TOC never steals
 *   horizontal space from the content; it floats over it instead.
 */
import type { TableOfContentData } from "@tiptap/extension-table-of-contents";

/** Tiny classlist joiner — forest avoids a clsx dependency here. */
function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(" ");
}

interface TableOfContentsPanelProps {
  items: TableOfContentData;
  /** Called with a heading's stable id when the user picks an entry. */
  onSelect: (id: string) => void;
  variant: "sidebar" | "overlay";
  /** Overlay only: whether the panel is slid open. Ignored for `sidebar`. */
  open?: boolean;
  /** Extra classes — the consumer uses this for container-query visibility. */
  className?: string;
}

function TocList({
  items,
  onSelect,
}: Pick<TableOfContentsPanelProps, "items" | "onSelect">) {
  return (
    <>
      <div className="mb-2 px-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        On this page
      </div>
      <ul className="flex flex-col gap-0.5">
        {items.map((item) => (
          // Two headings can share a slug ("Fireplace" in two rooms); their
          // positions can't.
          <li key={item.pos}>
            <a
              // Real href to the heading's slug id so the link is
              // copyable/shareable straight from the browser; the editor
              // intercepts the click to scroll and sync the URL hash itself.
              href={`#${item.id}`}
              onClick={(event) => {
                event.preventDefault();
                onSelect(item.id);
              }}
              // Indent by hierarchical depth so a doc that skips H1→H3 doesn't
              // over-indent (`level` is the collapsed depth, not raw heading).
              style={{ paddingLeft: `${(item.level - 1) * 0.75 + 0.5}rem` }}
              className={cx(
                "block truncate rounded py-1 pr-2 no-underline transition-colors hover:bg-muted",
                item.isActive
                  ? "font-medium text-foreground"
                  : "text-muted-foreground",
              )}
              title={item.textContent}
            >
              {item.textContent || "Untitled"}
            </a>
          </li>
        ))}
      </ul>
    </>
  );
}

export function TableOfContentsPanel({
  items,
  onSelect,
  variant,
  open = false,
  className,
}: TableOfContentsPanelProps) {
  if (variant === "sidebar") {
    return (
      <nav
        aria-label="Table of contents"
        className={cx(
          "w-56 shrink-0 overflow-auto border-l border-border p-3 text-sm",
          className,
        )}
      >
        <TocList items={items} onSelect={onSelect} />
      </nav>
    );
  }

  // Overlay: parked above the content area (`-translate-y-full`) and clipped
  // by the pane's `overflow-hidden`, so toggling `open` slides it down into
  // view without displacing the prose underneath.
  return (
    <nav
      aria-label="Table of contents"
      aria-hidden={!open}
      className={cx(
        // Flat — the border + solid background separate it from the content
        // below; no drop shadow (nothing else in the editor carries depth).
        "absolute right-0 top-0 z-20 flex max-h-full w-64 max-w-[85%] flex-col overflow-auto border-b border-l border-border bg-background p-3 text-sm transition-transform duration-200 ease-out",
        open ? "translate-y-0" : "-translate-y-full",
        !open && "pointer-events-none",
        className,
      )}
    >
      <TocList items={items} onSelect={onSelect} />
    </nav>
  );
}
